import { Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router";
import { useTheme } from "@/lib/theme";

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="rounded-full text-muted-foreground hover:text-foreground"
    >
      {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
    </Button>
  );
}

export function Wordmark({ className = "" }: { className?: string }) {
  return (
    <Link
      href="/"
      className={`inline-flex min-h-11 items-center gap-2 font-semibold tracking-tight ${className}`}
    >
      <span className="relative grid size-7 place-items-center rounded-full bg-brand text-[13px] text-primary-foreground">
        ♥
      </span>
      <span className="text-[17px]">Do We Match</span>
    </Link>
  );
}

export function Shell({
  children,
  nav,
  wide = false,
}: {
  children: ReactNode;
  nav?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="relative min-h-screen">
      <div className="aurora" aria-hidden="true" />

      <header
        // Keyboard nav measures this to know how much of the viewport top is
        // covered before scrolling a question into view.
        data-app-bar=""
        className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl"
      >
        <div
          className={`mx-auto flex h-14 items-center justify-between gap-4 px-5 ${
            wide ? "max-w-6xl" : "max-w-3xl"
          }`}
        >
          <Wordmark />
          <div className="flex items-center gap-1.5">
            {nav}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className={`mx-auto px-5 ${wide ? "max-w-6xl" : "max-w-3xl"}`}>{children}</main>

      <footer className="mt-20 border-t border-border/60 py-8">
        <div
          className={`mx-auto flex flex-col gap-2 px-5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between ${
            wide ? "max-w-6xl" : "max-w-3xl"
          }`}
        >
          <span>Do We Match — a dating app you're not supposed to use.</span>
          <span>No photos. No feed. No chat. Percentages for strangers, answers for matches.</span>
        </div>
      </footer>
    </div>
  );
}
